import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PlaceOutlined, WarningAmberRounded } from '@mui/icons-material';
import { Box, Tooltip } from '@mui/material';
import { KeyboardEvent, MouseEvent, useMemo } from 'react';
import { DispatchCard as DispatchCardType } from 'src/entities/dispatch';
import { GeoAlertInfo } from 'src/utils/geoAlerts';
import { getInfoMetricValue } from 'src/utils/metricHelpers';

import { DisplayMetric, InfoMetric } from './DisplayMetricToggle';

const INFO_METRIC_KEYS: InfoMetric[] = [
  'origin_dest',
  'section_pref',
  'section_city',
  'address',
  'shipper',
  'time_window',
  'conditions',
];

const integerFormatter = new Intl.NumberFormat('ja-JP');
const decimalFormatter = new Intl.NumberFormat('ja-JP', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

interface DispatchCardProps {
  card: DispatchCardType;
  isDragging?: boolean;
  selected?: boolean;
  showDuplicate?: boolean;
  routeDeviation?: boolean;
  totalCases?: number;
  displayMetrics?: Set<DisplayMetric>;
  geoAlert?: GeoAlertInfo;
  /** 直近のドロップで動いたカード。true の間は強調 outline + パルス。 */
  recentlyMoved?: boolean;
  onSelect?: (event: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => void;
}

function formatMetrics(card: Extract<DispatchCardType, { type: 'delivery' }>, metrics?: Set<DisplayMetric>) {
  const effectiveMetrics = metrics ?? new Set<DisplayMetric>(['quantity']);
  // 固定ルートの土台 (fr-template) は本物の案件ではないため、数量・重量・体積を
  // すべて "--" 表示にする (route-planner サンプル準拠)。
  // 本日なし (no-delivery) も同様に値なし扱い。
  // 配車済み (matched) と D&D 配置直後 (template) は legRun 実値をそのまま表示する。
  // legRun に値が無い (null) ときは "--" を出す (cases × 係数の推定値は出さない)。
  const isFrTemplate = card.status === 'fr-template';
  const isNoDelivery = card.status === 'no-delivery';
  const cases = isFrTemplate || isNoDelivery
    ? null
    : card.todayCases ?? card.cases ?? null;
  const kg = isFrTemplate || isNoDelivery ? null : card.weight ?? null;
  const m3 = isFrTemplate || isNoDelivery ? null : card.volume ?? null;
  const parts: string[] = [];
  if (effectiveMetrics.has('quantity'))
    parts.push(cases !== null ? `${cases} cs` : '-- cs');
  if (effectiveMetrics.has('weight'))
    parts.push(kg !== null ? `${integerFormatter.format(Math.round(kg))}kg` : '--kg');
  if (effectiveMetrics.has('volume'))
    parts.push(m3 !== null ? `${decimalFormatter.format(m3)}m³` : '--m³');
  return parts;
}

function getStatusLabel(card: Extract<DispatchCardType, { type: 'delivery' }>) {
  // fr-template (固定ルートの土台) と template (D&D 配置後 timeline 未反映) はどちらも
  // 「未反映」表示で揃える (route-planner サンプル準拠)。
  if (card.status === 'template' || card.status === 'fr-template') return '未反映';
  if (card.status === 'no-delivery') return '本日なし';
  return null;
}

const selectedRing = {
  boxShadow: '0 0 0 2px hsl(var(--background)), 0 0 0 4px hsl(var(--primary))',
};

// ドラッグドロップ後に「カードがどこに行ったか」を一定時間ハイライトするアニメーション。
// 1.4s × 2 = 2.8s 間、左右の縦バーが primary 色でパルスする (上下は付けない)。
// 既存 boxShadow ('0 1px 2px ...') を維持しつつ、x 方向のみ追加で primary バーを乗せる。
const recentlyMovedSx = {
  '@keyframes recentlyMovedPulse': {
    '0%': {
      boxShadow:
        '0 1px 2px 0 rgb(0 0 0 / 0.05), 2px 0 0 0 hsl(var(--primary) / 0.85), -2px 0 0 0 hsl(var(--primary) / 0.85)',
    },
    '60%': {
      boxShadow:
        '0 1px 2px 0 rgb(0 0 0 / 0.05), 3px 0 0 0 hsl(var(--primary) / 0.6), -3px 0 0 0 hsl(var(--primary) / 0.6)',
    },
    '100%': {
      boxShadow:
        '0 1px 2px 0 rgb(0 0 0 / 0.05), 2px 0 0 0 hsl(var(--primary) / 0.4), -2px 0 0 0 hsl(var(--primary) / 0.4)',
    },
  },
  animation: 'recentlyMovedPulse 1.4s ease-out 2',
  boxShadow:
    '0 1px 2px 0 rgb(0 0 0 / 0.05), 2px 0 0 0 hsl(var(--primary) / 0.4), -2px 0 0 0 hsl(var(--primary) / 0.4)',
  position: 'relative' as const,
  zIndex: 1,
};

export function DispatchCardView({
  card,
  isDragging,
  selected = false,
  showDuplicate = false,
  routeDeviation = false,
  totalCases,
  displayMetrics,
  geoAlert,
  recentlyMoved = false,
  onSelect,
}: DispatchCardProps) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.key === 'Enter' || event.key === ' ') && onSelect) {
      event.preventDefault();
      onSelect(event);
    }
  };

  const activeInfoMetrics = useMemo(() => {
    if (!displayMetrics || card.type !== 'delivery') return [];
    return INFO_METRIC_KEYS.filter((k) => displayMetrics.has(k));
  }, [displayMetrics, card.type]);

  const infoText = useMemo(() => {
    if (card.type !== 'delivery' || activeInfoMetrics.length === 0) return '';
    return activeInfoMetrics
      .map((m) => getInfoMetricValue(m, card))
      .filter(Boolean)
      .join(' / ');
  }, [card, activeInfoMetrics]);

  // fr-template は固定ルート由来の「土台」カードで、本物の案件ではないため移動不可。
  // no-delivery / loading 同様 sortable の id 末尾に -nodrag を付け、useSortable も disabled にする。
  const isDraggable =
    card.type === 'delivery' && card.status !== 'no-delivery' && card.status !== 'fr-template';
  const sortableId = isDraggable ? card.id : `${card.id}-nodrag`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isSortableDragging } = useSortable({
    id: sortableId,
    disabled: !isDraggable,
  });

  if (card.type === 'loading') {
    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={handleKeyDown}
        sx={{
          height: 72,
          width: 160,
          flexShrink: 0,
          borderRadius: '6px',
          border: '1px solid hsl(var(--border) / 0.6)',
          borderLeft: '4px solid hsl(var(--card-loading-accent) / 0.5)',
          p: 1.5,
          bgcolor: 'hsl(var(--card-loading) / 0.4)',
          boxShadow: 'none',
          userSelect: 'none',
          transition: 'all 0.2s',
          ...(selected && selectedRing),
          ...(recentlyMoved && recentlyMovedSx),
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: '-0.01em',
            color: 'hsl(var(--card-loading-foreground) / 0.8)',
          }}
        >
          {card.locationName || '積地'}
        </Box>
        <Box
          component="span"
          sx={{
            mt: 1,
            display: 'block',
            fontSize: 12,
            fontFamily: 'monospace',
            fontVariantNumeric: 'tabular-nums',
            color: 'hsl(var(--card-loading-foreground) / 0.65)',
          }}
        >
          {totalCases != null ? `${totalCases} cs` : '-- cs'}
        </Box>
      </Box>
    );
  }

  const dragging = isDragging || isSortableDragging;
  const isTemplate = card.status === 'template';
  const isFrTemplate = card.status === 'fr-template';
  const isNoDelivery = card.status === 'no-delivery';
  const statusLabel = getStatusLabel(card);

  const highlightSx = isNoDelivery
    ? { bgcolor: 'hsl(var(--card-inactive))', opacity: 0.55, filter: 'grayscale(1)' }
    : isFrTemplate
      // 固定ルートの仮カード (土台): 薄く点線枠。割当済みカードと区別するため影は無し (= 平面表示)。
      ? {
          bgcolor: 'hsl(var(--card))',
          border: '1px dashed hsl(var(--border))',
          opacity: 0.6,
          boxShadow: 'none',
        }
      : routeDeviation
        ? { border: '1px solid hsl(var(--status-warning-accent) / 0.28)', bgcolor: 'hsl(var(--status-warning) / 0.95)' }
        : isTemplate
          ? { bgcolor: 'hsl(var(--card))', border: '1px dashed hsl(var(--border))' }
          : { bgcolor: 'hsl(var(--card))', '&:hover': { border: '1px solid hsl(var(--primary) / 0.3)' } };

  const hasInfoText = infoText.length > 0;
  const hasValueMetrics = formatMetrics(card, displayMetrics).length > 0;

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...(isDraggable ? attributes : {})}
      {...(isDraggable ? listeners : {})}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      sx={{
        height: 72,
        width: 160,
        flexShrink: 0,
        borderRadius: '6px',
        p: 1.5,
        cursor: isDraggable ? 'grab' : 'default',
        userSelect: 'none',
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
        transition: 'all 0.2s',
        '&:active': isDraggable ? { cursor: 'grabbing' } : {},
        ...highlightSx,
        ...(selected && selectedRing),
        ...(recentlyMoved && recentlyMovedSx),
        ...(dragging && {
          zIndex: 50,
          transform: 'rotate(1deg) scale(1.02)',
          opacity: 0.6,
          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
        }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1, '& > * + *': { mt: 0.5 } }}>
          {(routeDeviation || showDuplicate) && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
              {routeDeviation && !isNoDelivery && (
                <Box
                  component="span"
                  sx={{
                    borderRadius: '2px',
                    bgcolor: 'hsl(var(--status-warning-accent) / 0.14)',
                    px: 0.5,
                    py: '2px',
                    fontSize: 9,
                    fontWeight: 600,
                    lineHeight: 1,
                    color: 'hsl(var(--status-warning-foreground))',
                  }}
                >
                  固定ルート外
                </Box>
              )}
              {showDuplicate && !isNoDelivery && (
                <Box
                  component="span"
                  sx={{
                    borderRadius: '2px',
                    bgcolor: 'hsl(var(--primary) / 0.12)',
                    px: 0.5,
                    py: '2px',
                    fontSize: 9,
                    fontWeight: 600,
                    lineHeight: 1,
                    color: 'hsl(var(--primary))',
                  }}
                >
                  他車両での取扱あり
                </Box>
              )}
            </Box>
          )}
          <Box
            component="span"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              color: isNoDelivery ? 'hsl(var(--card-inactive-foreground))' : 'hsl(var(--card-foreground))',
            }}
          >
            {card.storeName}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexShrink: 0, alignItems: 'flex-start', gap: '2px' }}>
          {geoAlert && !isNoDelivery && (
            <Tooltip title={<Box component="span" sx={{ fontSize: 12, lineHeight: 1.6 }}>{geoAlert.message}</Box>} placement="top" enterDelay={120}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  height: 16,
                  width: 16,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '2px',
                  color: geoAlert.level === 'strong' ? 'hsl(var(--status-error-accent))' : 'hsl(var(--status-warning-accent))',
                }}
              >
                <PlaceOutlined sx={{ fontSize: 12 }} />
              </Box>
            </Tooltip>
          )}
          {card.hasAlert && !isNoDelivery && (
            <Tooltip title={<Box component="span" sx={{ fontSize: 12, lineHeight: 1.6 }}>指定時間が超過しています。</Box>} placement="top" enterDelay={120}>
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  height: 16,
                  width: 16,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '2px',
                  color: 'hsl(var(--status-error-accent))',
                }}
              >
                <WarningAmberRounded sx={{ fontSize: 12 }} />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>

      {hasValueMetrics && (
        <Box
          sx={{
            mt: 0.5,
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: 1,
            fontSize: 10,
            fontFamily: 'monospace',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.01em',
            lineHeight: 1.2,
            color: isNoDelivery ? 'hsl(var(--card-inactive-foreground))' : 'hsl(var(--muted-foreground))',
          }}
        >
          {formatMetrics(card, displayMetrics).map((m, i) => (
            <Box component="span" key={i}>
              {m}
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ mt: '2px', display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 9, lineHeight: 1.2 }}>
        {statusLabel && !hasInfoText && (
          <Box
            component="span"
            sx={{ color: isNoDelivery ? 'hsl(var(--card-inactive-foreground))' : 'hsl(var(--muted-foreground))' }}
          >
            {statusLabel}
          </Box>
        )}
        {hasInfoText && (
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              color: isNoDelivery ? 'hsl(var(--card-inactive-foreground))' : 'hsl(var(--muted-foreground) / 0.8)',
            }}
          >
            {infoText}
          </Box>
        )}
      </Box>
    </Box>
  );
}
